// ✅ MIGRATED TO V2 DESIGN SYSTEM - Volvo/Teal Theme
/**
 * Composant de gestion des politiques de relance
 * Architecture 3 couches: Component → Hook → API Client → HTTP
 */

import React, { useState } from 'react';
import { useReminderPoliciesManagement } from '@/hooks/useReminderPoliciesManagement';
import {
  Bell,
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  ToggleLeft,
  ToggleRight,
  Mail,
  Clock,
  FileText,
  DollarSign,
  Info,
} from 'lucide-react';
import { ReminderPolicy, ReminderLevel } from '@/utils/reminderPoliciesAPI';
import { Button, Spinner, Badge } from '@/components/ui-v2';
import { confirm } from '@/stores/confirmStore';
import { logger } from '@/utils/logger';

const ReminderPoliciesManagement: React.FC = () => {
  const {
    filteredPolicies,
    invoicePolicies,
    quotePolicies,
    loading,
    showForm,
    editingPolicy,
    entityTypeFilter,
    activeOnlyFilter,
    setShowForm,
    setEditingPolicy,
    setEntityTypeFilter,
    setActiveOnlyFilter,
    createPolicy,
    updatePolicy,
    deletePolicy,
    toggleActive,
  } = useReminderPoliciesManagement();

  // Form state
  const [formData, setFormData] = useState<{
    entityType: 'invoice' | 'quote';
    levels: ReminderLevel[];
  }>({
    entityType: 'invoice',
    levels: [{ daysAfter: 7, emailTemplate: { subject: '', body: '' } }],
  });

  // Card classes V2
  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1
    transition-all duration-500`;

  // Input classes V2
  const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none placeholder:text-gray-400`;

  // ==================== HANDLERS ====================

  const handleCreate = () => {
    setEditingPolicy(null);
    setFormData({
      entityType: 'invoice',
      levels: [{ daysAfter: 7, emailTemplate: { subject: '', body: '' } }],
    });
    setShowForm(true);
  };

  const handleEdit = (policy: ReminderPolicy) => {
    setEditingPolicy(policy);
    setFormData({
      entityType: policy.entityType as 'invoice' | 'quote',
      levels: policy.levels,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string, entityType: string) => {
    const confirmed = await confirm({
      title: 'Supprimer la politique',
      message: `Etes-vous sur de vouloir supprimer cette politique de relance ${entityType === 'invoice' ? 'factures' : 'devis'} ?`,
      variant: 'danger',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
    });
    if (confirmed) {
      await deletePolicy(id);
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    await toggleActive(id, isActive);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingPolicy) {
        await updatePolicy(editingPolicy.id, {
          levels: formData.levels,
        });
      } else {
        await createPolicy(formData);
      }
    } catch (error) {
      logger.error('Form submission error:', error);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPolicy(null);
    setFormData({
      entityType: 'invoice',
      levels: [{ daysAfter: 7, emailTemplate: { subject: '', body: '' } }],
    });
  };

  const handleAddLevel = () => {
    setFormData({
      ...formData,
      levels: [
        ...formData.levels,
        { daysAfter: 0, emailTemplate: { subject: '', body: '' } },
      ],
    });
  };

  const handleRemoveLevel = (index: number) => {
    setFormData({
      ...formData,
      levels: formData.levels.filter((_, i) => i !== index),
    });
  };

  const handleLevelChange = (index: number, field: string, value: any) => {
    const newLevels = [...formData.levels];
    if (field === 'daysAfter') {
      newLevels[index] = { ...newLevels[index], daysAfter: parseInt(value) || 0 };
    } else if (field === 'subject') {
      newLevels[index] = {
        ...newLevels[index],
        emailTemplate: {
          ...newLevels[index].emailTemplate!,
          subject: value,
        },
      };
    } else if (field === 'body') {
      newLevels[index] = {
        ...newLevels[index],
        emailTemplate: {
          ...newLevels[index].emailTemplate!,
          body: value,
        },
      };
    }
    setFormData({ ...formData, levels: newLevels });
  };

  // ==================== RENDER HELPERS ====================

  const renderPolicyCard = (policy: ReminderPolicy) => {
    const Icon = policy.entityType === 'invoice' ? DollarSign : FileText;
    const iconBg = policy.entityType === 'invoice' ? 'bg-teal-100' : 'bg-blue-100';
    const iconColor = policy.entityType === 'invoice' ? 'text-teal-600' : 'text-blue-600';

    return (
      <div key={policy.id} className={cardClasses}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-2xl ${iconBg}`}>
              <Icon className={`w-6 h-6 ${iconColor}`} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                {policy.entityType === 'invoice' ? 'Relances Factures' : 'Relances Devis'}
              </h3>
              <p className="text-sm text-gray-500">
                {policy.levels.length} niveau{policy.levels.length > 1 ? 'x' : ''}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handleToggleActive(policy.id, policy.isActive)}
              className={`p-2 rounded-xl transition-all duration-300 ${
                policy.isActive
                  ? 'bg-teal-100 hover:bg-teal-200 text-teal-700'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-500'
              }`}
              title={policy.isActive ? 'Désactiver' : 'Activer'}
            >
              {policy.isActive ? (
                <ToggleRight className="w-5 h-5" />
              ) : (
                <ToggleLeft className="w-5 h-5" />
              )}
            </button>
            <button
              onClick={() => handleEdit(policy)}
              className="p-2 rounded-xl bg-teal-50 hover:bg-teal-100 text-teal-600 transition-all duration-300"
              title="Modifier"
            >
              <Edit className="w-5 h-5" />
            </button>
            <button
              onClick={() => handleDelete(policy.id, policy.entityType)}
              className="p-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 transition-all duration-300"
              title="Supprimer"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {policy.levels.map((level, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 bg-white/40 rounded-2xl border border-white/50 hover:bg-white/60 transition-all duration-300"
            >
              <div className="p-1.5 bg-teal-100 rounded-lg">
                <Clock className="w-4 h-4 text-teal-600" />
              </div>
              <span className="text-sm font-medium text-gray-700">
                Niveau {index + 1}:
              </span>
              <span className="text-sm text-gray-600">
                Après {level.daysAfter} jour{level.daysAfter > 1 ? 's' : ''}
              </span>
              {level.emailTemplate && (
                <span className="text-xs text-gray-500 truncate flex-1">
                  • {level.emailTemplate.subject || 'Sans sujet'}
                </span>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
          <span>Créé le {new Date(policy.createdAt).toLocaleDateString('fr-FR')}</span>
          <Badge variant={policy.isActive ? 'success' : 'pending'}>
            {policy.isActive ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      </div>
    );
  };

  const renderForm = () => (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-white/40">
        {/* Header du modal */}
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">
            {editingPolicy ? 'Modifier la politique' : 'Nouvelle politique de relance'}
          </h2>
          <button
            onClick={handleCancel}
            className="text-white hover:bg-white/20 rounded-xl p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Corps du modal */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(90vh-80px)] overflow-y-auto">
          {/* Type d'entité */}
          {!editingPolicy && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Type de document
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, entityType: 'invoice' })}
                  className={`p-4 rounded-2xl border-2 transition-all duration-300 ${
                    formData.entityType === 'invoice'
                      ? 'border-teal-500 bg-teal-50/50 shadow-lg shadow-teal-100/50'
                      : 'border-white/50 bg-white/40 hover:border-teal-400 hover:bg-white hover:shadow-lg'
                  }`}
                >
                  <div className={`p-3 rounded-xl ${formData.entityType === 'invoice' ? 'bg-teal-100' : 'bg-gray-100'} mx-auto w-fit mb-2`}>
                    <DollarSign className={`w-6 h-6 ${formData.entityType === 'invoice' ? 'text-teal-600' : 'text-gray-400'}`} />
                  </div>
                  <span className={`font-medium ${formData.entityType === 'invoice' ? 'text-teal-700' : 'text-gray-600'}`}>
                    Factures
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, entityType: 'quote' })}
                  className={`p-4 rounded-2xl border-2 transition-all duration-300 ${
                    formData.entityType === 'quote'
                      ? 'border-blue-500 bg-blue-50/50 shadow-lg shadow-blue-100/50'
                      : 'border-white/50 bg-white/40 hover:border-blue-400 hover:bg-white hover:shadow-lg'
                  }`}
                >
                  <div className={`p-3 rounded-xl ${formData.entityType === 'quote' ? 'bg-blue-100' : 'bg-gray-100'} mx-auto w-fit mb-2`}>
                    <FileText className={`w-6 h-6 ${formData.entityType === 'quote' ? 'text-blue-600' : 'text-gray-400'}`} />
                  </div>
                  <span className={`font-medium ${formData.entityType === 'quote' ? 'text-blue-700' : 'text-gray-600'}`}>
                    Devis
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Niveaux de relance */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Niveaux de relance
              </label>
              <Button
                type="button"
                onClick={handleAddLevel}
                variant="secondary"
                size="sm"
                icon={<Plus className="w-4 h-4" />}
              >
                Ajouter un niveau
              </Button>
            </div>

            <div className="space-y-4">
              {formData.levels.map((level, index) => (
                <div key={index} className="p-5 bg-white/40 border border-white/50 rounded-2xl hover:bg-white/60 transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <div className="p-1.5 bg-teal-100 rounded-lg">
                        <Bell className="w-4 h-4 text-teal-600" />
                      </div>
                      Niveau {index + 1}
                    </h4>
                    {formData.levels.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveLevel(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-4">
                    {/* Délai */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Clock className="w-4 h-4 inline mr-2 text-teal-600" />
                        Délai (jours)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={level.daysAfter}
                        onChange={(e) => handleLevelChange(index, 'daysAfter', e.target.value)}
                        className={inputClasses}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Nombre de jours après {formData.entityType === 'invoice' ? 'la date d\'échéance' : 'la création du devis'}
                      </p>
                    </div>

                    {/* Sujet de l'email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Mail className="w-4 h-4 inline mr-2 text-teal-600" />
                        Sujet de l'email
                      </label>
                      <input
                        type="text"
                        value={level.emailTemplate?.subject || ''}
                        onChange={(e) => handleLevelChange(index, 'subject', e.target.value)}
                        placeholder="Ex: Rappel de paiement - Facture #{{invoiceNumber}}"
                        className={inputClasses}
                        required
                      />
                    </div>

                    {/* Corps de l'email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Corps de l'email
                      </label>
                      <textarea
                        value={level.emailTemplate?.body || ''}
                        onChange={(e) => handleLevelChange(index, 'body', e.target.value)}
                        placeholder={`Bonjour {{clientName}},\n\nNous vous rappelons que ${formData.entityType === 'invoice' ? 'votre facture' : 'votre devis'} #{{${formData.entityType === 'invoice' ? 'invoiceNumber' : 'quoteNumber'}}} est en attente...\n\nCordialement`}
                        rows={6}
                        className={`${inputClasses} resize-none font-mono text-sm`}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Variables disponibles: {'{{'}clientName{'}}'}, {'{{'}invoiceNumber{'}}'}, {'{{'}quoteNumber{'}}'}, {'{{'}amount{'}}'}, {'{{'}dueDate{'}}'}, {'{{'}daysOverdue{'}}'}, {'{{'}companyName{'}}'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-white/30">
            <Button
              type="button"
              onClick={handleCancel}
              variant="ghost"
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="secondary"
              className="flex-1"
              icon={<Save className="w-4 h-4" />}
            >
              {editingPolicy ? 'Mettre à jour' : 'Créer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );

  // ==================== MAIN RENDER ====================

  if (loading && filteredPolicies.length === 0) {
    return (
      <div className="px-8 py-12 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-teal-100 rounded-2xl">
            <Bell className="w-8 h-8 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Politiques de relance</h1>
            <p className="text-gray-500 mt-0.5">
              Configurez les relances automatiques pour vos factures et devis
            </p>
          </div>
        </div>
        <Button
          onClick={handleCreate}
          variant="secondary"
          icon={<Plus className="w-4 h-4" />}
        >
          Nouvelle politique
        </Button>
      </div>

      {/* Info Box */}
      <div className="bg-teal-50/30 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-5 hover:bg-teal-50/50 transition-all duration-300">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-teal-100 rounded-xl">
            <Info className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h4 className="font-semibold text-teal-900">Relances automatiques</h4>
            <p className="text-sm text-teal-800 mt-1">
              Définissez des niveaux de relance pour automatiser l'envoi d'emails à vos clients.
              Chaque niveau correspond à un délai après la date d'échéance (factures) ou la date de création (devis).
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={cardClasses}>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setEntityTypeFilter('all')}
            className={`px-4 py-2.5 rounded-2xl font-medium transition-all duration-300 ${
              entityTypeFilter === 'all'
                ? 'bg-teal-600 text-white shadow-lg shadow-teal-200/50'
                : 'bg-white/40 text-gray-700 hover:bg-white hover:shadow-lg'
            }`}
          >
            Tous ({filteredPolicies.length})
          </button>
          <button
            onClick={() => setEntityTypeFilter('invoice')}
            className={`px-4 py-2.5 rounded-2xl font-medium transition-all duration-300 ${
              entityTypeFilter === 'invoice'
                ? 'bg-teal-600 text-white shadow-lg shadow-teal-200/50'
                : 'bg-white/40 text-gray-700 hover:bg-white hover:shadow-lg'
            }`}
          >
            Factures ({invoicePolicies.length})
          </button>
          <button
            onClick={() => setEntityTypeFilter('quote')}
            className={`px-4 py-2.5 rounded-2xl font-medium transition-all duration-300 ${
              entityTypeFilter === 'quote'
                ? 'bg-teal-600 text-white shadow-lg shadow-teal-200/50'
                : 'bg-white/40 text-gray-700 hover:bg-white hover:shadow-lg'
            }`}
          >
            Devis ({quotePolicies.length})
          </button>

          <div className="h-8 w-px bg-white/50 mx-2" />

          <button
            onClick={() => setActiveOnlyFilter(!activeOnlyFilter)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl font-medium transition-all duration-300 ${
              activeOnlyFilter
                ? 'bg-teal-100 text-teal-700 border border-teal-300'
                : 'bg-white/40 text-gray-700 hover:bg-white hover:shadow-lg'
            }`}
          >
            {activeOnlyFilter ? (
              <ToggleRight className="w-4 h-4" />
            ) : (
              <ToggleLeft className="w-4 h-4" />
            )}
            {activeOnlyFilter ? 'Actives uniquement' : 'Toutes'}
          </button>
        </div>
      </div>

      {/* Policies Grid */}
      {filteredPolicies.length === 0 ? (
        <div className={`${cardClasses} text-center py-12`}>
          <div className="p-4 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
            <Bell className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Aucune politique de relance
          </h3>
          <p className="text-gray-600 mb-6">
            Créez votre première politique pour automatiser vos relances
          </p>
          <div className="flex justify-center">
            <Button
              onClick={handleCreate}
              variant="secondary"
              icon={<Plus className="w-4 h-4" />}
            >
              Créer une politique
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredPolicies.map(renderPolicyCard)}
        </div>
      )}

      {/* Form Modal */}
      {showForm && renderForm()}
    </div>
  );
};

export default ReminderPoliciesManagement;
